// End-of-service / offboarding (Phase B) — HR view. Initiate a separation, work
// the clearance checklist, then settle (persisted gratuity + leave encashment)
// and print the experience letter.
import { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField } from '@mui/material';
import { Plus, UserMinus, Printer, Check } from 'lucide-react';
import { Card, SectionTitle, CenterSpinner, PrimaryBtn, OutlineBtn, Pill } from './ui';
import { EmptyState } from './components';
import { AG } from './theme/tokens';
import { api } from './api';

const TYPES = ['Resignation', 'Termination', 'Retirement'];
const money = (n) => `Rs ${Number(n || 0).toLocaleString('en-IN')}`;
const statTone = (s) => (s === 'Settled' ? 'success' : 'warning');

function printLetter(body) {
  const w = window.open('', '_blank', 'width=720,height=900');
  if (!w) return;
  w.document.write(`<pre style="font-family:Georgia,serif;font-size:14px;line-height:1.6;white-space:pre-wrap;padding:48px;max-width:680px;margin:auto">${(body || '').replace(/</g, '&lt;')}</pre>`);
  w.document.close(); w.focus(); w.print();
}

export default function Offboarding({ showToast }) {
  const [rows, setRows] = useState(null);
  const [people, setPeople] = useState([]);
  const [form, setForm] = useState(null);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.offboardings().then(setRows).catch(() => setRows([]));
  useEffect(() => { load(); api.employees().then((r) => setPeople((Array.isArray(r) ? r : r.rows || []).filter((p) => p.status !== 'Inactive'))).catch(() => setPeople([])); }, []);
  const openDetail = (id) => api.offboarding(id).then(setDetail).catch((e) => showToast?.(e.message));

  const initiate = async () => {
    if (!form.employeeId || !form.lastDay || busy) return; setBusy(true);
    try { const r = await api.initiateOffboarding(form); showToast?.('Offboarding initiated'); setForm(null); await load(); openDetail(r.id); }
    catch (e) { showToast?.(e.message); } finally { setBusy(false); }
  };
  const toggleTask = async (t) => { try { await api.toggleClearanceTask(detail.id, t.id, !t.done); openDetail(detail.id); } catch (e) { showToast?.(e.message); } };
  const settle = async () => {
    if (busy) return; setBusy(true);
    try { await api.settleOffboarding(detail.id); showToast?.('Settled — employee offboarded'); await load(); openDetail(detail.id); }
    catch (e) { showToast?.(e.message); } finally { setBusy(false); }
  };

  if (!rows) return <CenterSpinner />;

  return (
    <div className="space-y-4 fade-up">
      <div className="flex items-center justify-between" style={{ gap: 12, flexWrap: 'wrap' }}>
        <SectionTitle title="Offboarding" sub="Separations, clearance & final settlement" />
        <PrimaryBtn onClick={() => setForm({ employeeId: '', type: 'Resignation', lastDay: '', reason: '' })}><Plus size={14} style={{ marginRight: 6 }} /> New offboarding</PrimaryBtn>
      </div>

      {rows.length === 0 ? (
        <Card><EmptyState icon={UserMinus} title="No offboardings" desc="Initiate a separation to run clearance and settlement." /></Card>
      ) : (
        <div style={{ display: 'grid', gap: 8 }}>
          {rows.map((o) => (
            <Card key={o.id} style={{ padding: 14, cursor: 'pointer' }} onClick={() => openDetail(o.id)} className="flex items-center justify-between">
              <div>
                <div style={{ fontWeight: 600, color: AG.text }}>{o.employeeName} <span style={{ color: AG.n400, fontWeight: 400 }}>· {o.type}</span></div>
                <div style={{ fontSize: 12, color: AG.n500 }}>Last day {o.lastDay}{o.settlementTotal ? ` · settled ${money(o.settlementTotal)}` : ''}</div>
              </div>
              <Pill tone={statTone(o.status)} dot>{o.status}</Pill>
            </Card>
          ))}
        </div>
      )}

      {/* Initiate dialog */}
      <Dialog open={!!form} onClose={() => !busy && setForm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>New offboarding</DialogTitle>
        <DialogContent>
          {form && (
            <div style={{ paddingTop: 6 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: AG.n600 }}>Employee</div>
              <select value={form.employeeId} onChange={(e) => setForm({ ...form, employeeId: e.target.value })} className="ag-input" style={{ marginTop: 4, marginBottom: 12 }}>
                <option value="">Select employee…</option>
                {people.map((p) => <option key={p.id} value={p.id}>{p.name}{p.dept ? ` · ${p.dept}` : ''}</option>)}
              </select>
              <div className="grid grid-cols-2 gap-2" style={{ marginBottom: 12 }}>
                <div><div style={{ fontSize: 12, fontWeight: 600, color: AG.n600 }}>Type</div><select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="ag-input" style={{ marginTop: 4 }}>{TYPES.map((t) => <option key={t}>{t}</option>)}</select></div>
                <TextField size="small" type="date" label="Last day" InputLabelProps={{ shrink: true }} value={form.lastDay} onChange={(e) => setForm({ ...form, lastDay: e.target.value })} />
              </div>
              <TextField fullWidth size="small" label="Reason (optional)" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
            </div>
          )}
        </DialogContent>
        <DialogActions><OutlineBtn onClick={() => setForm(null)} disabled={busy}>Cancel</OutlineBtn><PrimaryBtn onClick={initiate} disabled={busy}>Initiate</PrimaryBtn></DialogActions>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="sm" fullWidth>
        {detail && (
          <>
            <DialogTitle>{detail.employeeName} · {detail.type}</DialogTitle>
            <DialogContent>
              <div style={{ fontSize: 12, color: AG.n500, marginBottom: 12 }}>Last day {detail.lastDay}{detail.reason ? ` · ${detail.reason}` : ''} · <Pill tone={statTone(detail.status)}>{detail.status}</Pill></div>

              <div style={{ fontWeight: 600, color: AG.text, marginBottom: 8 }}>Clearance checklist</div>
              <div style={{ display: 'grid', gap: 6, marginBottom: 16 }}>
                {detail.tasks.map((t) => (
                  <label key={t.id} className="flex items-center gap-2" style={{ fontSize: 13.5, color: AG.text, cursor: detail.status === 'Settled' ? 'default' : 'pointer' }}>
                    <input type="checkbox" checked={t.done} disabled={detail.status === 'Settled'} onChange={() => toggleTask(t)} />
                    <Pill tone="neutral">{t.owner}</Pill> {t.title}
                  </label>
                ))}
              </div>

              <div style={{ fontWeight: 600, color: AG.text, marginBottom: 8 }}>Final settlement {detail.settlement.final ? '' : '(estimate)'}</div>
              <Card style={{ padding: 14, marginBottom: 12 }}>
                <Row label={`Gratuity${detail.settlement.years != null ? ` · ${detail.settlement.years} yrs` : ''}`} value={money(detail.settlement.gratuity)} />
                <Row label={`Leave encashment (${detail.settlement.leaveDays || 0} days)`} value={money(detail.settlement.leaveEncashment)} />
                <Row label="Total" value={money(detail.settlement.total)} accent />
              </Card>
              <OutlineBtn onClick={() => printLetter(detail.experienceLetter)}><Printer size={14} style={{ marginRight: 6 }} /> Experience letter</OutlineBtn>
            </DialogContent>
            <DialogActions>
              <OutlineBtn onClick={() => setDetail(null)}>Close</OutlineBtn>
              {detail.status !== 'Settled' && <PrimaryBtn onClick={settle} disabled={busy || !detail.cleared}><Check size={14} style={{ marginRight: 6 }} /> {detail.cleared ? 'Settle & offboard' : 'Complete clearance first'}</PrimaryBtn>}
            </DialogActions>
          </>
        )}
      </Dialog>
    </div>
  );
}

function Row({ label, value, accent }) {
  return (
    <div className="flex items-center justify-between" style={{ fontSize: 14, padding: '8px 0', borderTop: accent ? `2px solid ${AG.n200}` : 'none', borderBottom: accent ? 'none' : `1px solid ${AG.divider}`, marginTop: accent ? 4 : 0 }}>
      <span style={{ color: AG.n500 }}>{label}</span>
      <span style={{ color: accent ? AG.primary : AG.text, fontWeight: accent ? 700 : 500 }}>{value}</span>
    </div>
  );
}
